import { Injectable } from '@angular/core';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';

/**
 * Injectable wrapper for Sentry SDK to enable proper testing.
 *
 * Sentry's ES6 exports are read-only and cannot be spied on directly in tests.
 * This service wraps Sentry calls to make them testable via dependency injection.
 *
 * @example
 * ```typescript
 * constructor(private sentry: SentryService) {}
 *
 * this.sentry.captureException(error, { extra: { userId: 123 } });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class SentryService {
  /**
   * Capture an exception and send to Sentry
   */
  captureException(
    error: unknown,
    captureContext?: Sentry.CaptureContext,
  ): string {
    return Sentry.captureException(error, captureContext);
  }

  /**
   * Capture a message and send to Sentry
   */
  captureMessage(
    message: string,
    captureContext?: Sentry.CaptureContext,
  ): string {
    return Sentry.captureMessage(message, captureContext);
  }

  /**
   * Set user context for Sentry events
   */
  setUser(user: Sentry.User | null): void {
    Sentry.setUser(user);
  }

  /**
   * Set additional context for Sentry events
   */
  setContext(name: string, context: Record<string, unknown> | null): void {
    Sentry.setContext(name, context);
  }

  /**
   * Add breadcrumb for debugging
   */
  addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
    Sentry.addBreadcrumb(breadcrumb);
  }
}
